import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';
import {
  Content,
  TDocumentDefinitions,
  TFontDictionary,
} from 'pdfmake/interfaces';
import { Style } from 'pdfmake/interfaces';

interface PdfGenerateOptions {
  element: HTMLElement;
  fileName: string;
  customConfig?: Partial<TDocumentDefinitions>;
  excludeElements?: string[];
  printData: PrintData;
}

interface PrintData {
  logo: string;
  orgDetail: {
    address: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
  };
  caseDetail?: {
    student: {
      firstName: string;
      lastName: string;
    };
    supervisorName: string;
  };
}

interface PdfStyles {
  [key: string]: Style;
  header: Style;
  subheader: Style;
  desc: Style;
  address: Style;
  title: Style;
  datalabel: Style;
  dataValue: Style;
  content: Style;
  columns: Style;
}

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  constructor() {
    (pdfMake.vfs as unknown as TFontDictionary) = pdfFonts;
  }

  private readonly BRAND_COLORS = {
    primary: '#17b26a',
    gray600: '#475467',
    gray900: '#101828',
    gray300: '#E4E7EC',
    background: '#F9FAFB',
  };

  private defaultStyles: PdfStyles = {
    header: {
      fontSize: 18,
      bold: true,
      color: this.BRAND_COLORS.primary,
      margin: [0, 0, 0, 10],
    },
    subheader: {
      fontSize: 14,
      bold: true,
      color: this.BRAND_COLORS.gray900,
      margin: [0, 10, 0, 5],
    },
    desc: {
      fontSize: 10,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.4,
      margin: [0, 0, 0, 5],
    },
    address: {
      fontSize: 10,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.4,
    },
    title: {
      fontSize: 16,
      bold: true,
      color: this.BRAND_COLORS.primary,
      margin: [0, 10, 0, 10],
    },
    datalabel: {
      fontSize: 11,
      bold: true,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.5,
    },
    dataValue: {
      fontSize: 11,
      color: this.BRAND_COLORS.gray900,
      lineHeight: 1.4,
    },
    content: {
      fontSize: 11,
      color: this.BRAND_COLORS.gray600,
      lineHeight: 1.6,
      margin: [0, 5, 0, 5],
    },
    columns: {
      columnGap: 20,
      margin: [0, 10, 0, 10],
    },
  };

  private defaultConfig(printData: PrintData): Partial<TDocumentDefinitions> {
    return {
      pageSize: 'A4',
      pageMargins: [40, 140, 40, 60],
      defaultStyle: {
        fontSize: 11,
        lineHeight: 1.4,
        color: this.BRAND_COLORS.gray600,
      },
      header: this.getHeaderDefinition(printData),
      footer: (currentPage: number) => ({
        columns: [
          {
            text: new Date().toLocaleDateString(),
            alignment: 'left',
            fontSize: 9,
            color: this.BRAND_COLORS.gray600,
          },
          {
            text: 'Powered by Insights to Behavior®',
            alignment: 'center',
            fontSize: 9,
            bold: true,
            color: this.BRAND_COLORS.primary,
          },
          {
            text: `Page ${currentPage}`,
            alignment: 'right',
            fontSize: 9,
            color: this.BRAND_COLORS.gray600,
          },
        ],
        margin: [40, 20, 40, 0],
      }),
      styles: this.defaultStyles,
      info: {
        title: 'Student Behavior Report',
        author: 'Insights to Behavior',
        subject: 'Student Assessment Report',
        keywords: 'behavior, student, assessment, report',
      },
    };
  }

  private getHeaderDefinition(printData: PrintData): Content {
    return {
      stack: [
        {
          columns: [
            {
              width: 150,
              image: printData.logo,
              fit: [120, 60],
              margin: [0, 0, 40, 0],
            },
            {
              width: '*',
              stack: [
                {
                  text: printData.orgDetail.address,
                  style: 'desc',
                  alignment: 'right',
                },
                {
                  text: [
                    `${printData.orgDetail.city}, `,
                    `${printData.orgDetail.state}, `,
                    `${printData.orgDetail.country} `,
                    printData.orgDetail.zipCode,
                  ],
                  style: 'address',
                  alignment: 'right',
                },
              ],
            },
          ],
          margin: [0, 0, 0, 20],
        },
        this.getHorizontalLine(),
        // {
        //   columns: [
        //     {
        //       width: '*',
        //       text: `${printData.caseDetail.student.firstName} ${printData.caseDetail.student.lastName}`,
        //       style: 'title',
        //     },
        //     {
        //       width: '*',
        //       text: [
        //         { text: 'Supervisor: ', style: 'datalabel' },
        //         {
        //           text: printData.caseDetail.supervisorName || '-',
        //           style: 'dataValue',
        //         },
        //       ],
        //       alignment: 'right',
        //     },
        //   ],
        //   margin: [0, 15, 0, 15],
        // },
        // this.getHorizontalLine(),
      ],
      margin: [40, 20, 40, 0],
    } as Content;
  }

  private getHorizontalLine() {
    return {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 0.5,
          lineColor: this.BRAND_COLORS.gray300,
        },
      ],
    };
  }
  async generatePDF(options: PdfGenerateOptions): Promise<void> {
    const {
      element,
      fileName,
      customConfig = {},
      excludeElements = [],
      printData,
    } = options;

    if (!element) {
      throw new Error('Element not found');
    }

    const tempElement = element.cloneNode(true) as HTMLElement;
    excludeElements.forEach((selector: string) => {
      const elementsToRemove = tempElement.querySelectorAll(selector);
      elementsToRemove.forEach(el => el.remove());
    });

    const pdfMakeContent = htmlToPdfmake(tempElement.innerHTML);

    const documentDefinition: TDocumentDefinitions = {
      ...this.defaultConfig(printData),
      ...customConfig,
      content: pdfMakeContent,
    };

    const pdf = pdfMake.createPdf(documentDefinition);
    pdf.download(fileName);
    return Promise.resolve();
  }
}
